<script setup lang="ts">
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { useGradeCalculationSettings } from '@/composables/gradeCalculation/settings/useGradeCalculationSettings';
import { useClassStudents } from '@/composables/student/useClassStudents';

import { PerformanceAssessmentDocumentationMode, ObservationTimelineEntryType } from '@/api/generated/api.enum';
import BookIcon from '@/assets/icons/BookIcon.vue';
import CompetencesIcon from '@/assets/icons/CompetencesIcon.vue';
import DocumentPenIcon from '@/assets/icons/DocumentPenIcon.vue';
import GroupIcon from '@/assets/icons/GroupIcon.vue';
import SupportMeasureIcon from '@/assets/icons/SupportMeasureIcon.vue';
import { useCompetenceAssessment } from '@/composables/competenceAssessment/useCompetenceAssessment';
import { useCompetenceDomain } from '@/composables/useCompetenceDomain';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { useDialog } from '@/core/dialogsystem/useDialog';
import PerformanceAssessmentEntryDetailDialog from '@/core/dialogs/performance-assessment-entry-detail/PerformanceAssessmentEntryDetailDialog.vue';
import {
  EPerformanceAssessmentCreationSource,
  useCreatePerformanceAssessmentEntry,
} from '@/composables/performanceAssessmentEntry/useCreatePerformanceAssessmentEntry';
import { ENoteCreationSource, useNote } from '@/composables/note/useNote';
import {
  ESupportMeasureCreationSource,
  useSupportMeasureEntry,
} from '@/composables/supportMeasureEntry/useSupportMeasureEntry';
import { useSupportMeasureEntryDialog } from '@/composables/supportMeasureEntry/useSupportMeasureEntryDialog';
import { useMobile } from '@/composables/useMobile';
import { usePerformanceAssessment } from '@/composables/usePerformanceAssessment';
import {
  EQuickEntryEscapeAction,
  EQuickEntryKind,
  EQuickEntryPopover,
  EQuickEntrySuggestionKind,
} from '@/composables/quickEntry/quickEntry.enums';
import {
  applyLastUsedQuickEntryType,
  buildQuickEntryCreateDto,
  buildQuickEntryNoteCreateDto,
  buildQuickEntrySupportMeasureCreateDto,
  getQuickEntryVisibilityHint,
  nextEscapeAction,
  shouldShowQuickEntryCollectiveRating,
  type IQuickEntryFixedContext,
  type IQuickEntryPreselection,
  type IQuickEntryVisibilityHint,
  type TQuickEntryCreatedPayload,
  type TQuickEntryPostCreateAction,
} from '@/composables/quickEntry/quickEntry.utils';
import { useQuickEntryDraft } from '@/composables/quickEntry/useQuickEntryDraft';
import {
  QuickEntryRequiredAction,
  type TQuickEntryRequiredAction,
} from '@/composables/quickEntry/quickEntry.validation';
import { useRequiredActionGuidance } from '@/composables/requiredAction/useRequiredActionGuidance';
import {
  useQuickEntrySuggestions,
  type IQuickEntrySuggestionItem,
} from '@/composables/quickEntry/useQuickEntrySuggestions';
import { useQuickEntrySupportMeasure } from '@/composables/quickEntry/useQuickEntrySupportMeasure';
import { useSupportMeasureFeature } from '@/composables/supportMeasureEntry/useSupportMeasureFeature';
import {
  isQuickEntryFocusTargetSelected,
  type IInlineQuickEntryComposerHandle,
  type TQuickEntryFocusTarget,
  useInlineQuickEntryComposerRegistry,
} from '@/composables/quickEntry/useInlineQuickEntryComposer';
import { useSchoolSubject } from '@/composables/schoolSubject/useSchoolSubject';
import { useLastViewed } from '@/composables/useLastViewed';
import BillingProBadge from '@/core/billing/BillingProBadge.vue';
import ComposerContextRow from '@/core/elements/ComposerContextRow.vue';
import ComposerInputRow from '@/core/elements/ComposerInputRow.vue';
import ComposerPanel from '@/core/elements/ComposerPanel.vue';
import ComposerTextInput from '@/core/elements/ComposerTextInput.vue';
import CustomSeparator from '@/core/elements/CustomSeparator.vue';
import InlineInfoHint from '@/core/elements/InlineInfoHint.vue';
import PerformanceAssessmentIcon from '@/core/elements/PerformanceAssessmentIcon.vue';
import SchoolSubjectBadge from '@/core/elements/SchoolSubjectBadge.vue';
import SuggestionPanel from '@/core/elements/SuggestionPanel.vue';
import { getSuggestionOptionId } from '@/core/elements/suggestionPanel.utils';
import UserAvatar from '@/core/UserAvatar.vue';
import SelectedCompetenceRatingRow from '@/core/competences/SelectedCompetenceRatingRow.vue';
import SelectedCompetenceRow from '@/core/competences/SelectedCompetenceRow.vue';
import PerformanceAssessmentOverallRating from '@/core/performanceAssessments/PerformanceAssessmentOverallRating.vue';
import CompetenceSelectionPopover from '@/core/selects/CompetenceSelectionPopover.vue';
import DateSelectionPopover from '@/core/selects/DateSelectionPopover.vue';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { resolveDialogTeleportTarget } from '@/utils/dom.utils';
import { EKeyboardKey } from '@/utils/menu.utils';
import { getPerformanceAssessmentName } from '@/utils/performance.utils';
import { isWholeClassStudentSelection } from '@/utils/students.utils';
import { onClickOutside } from '@vueuse/core';
import { computed, nextTick, onMounted, onUnmounted, ref, toRef, useTemplateRef, watch, type Component } from 'vue';
import { useI18n } from 'vue-i18n';
import QuickEntryBottomBar from './QuickEntryBottomBar.vue';
import QuickEntryCollectiveRatingHint from './QuickEntryCollectiveRatingHint.vue';
import QuickEntryTypePopover from './QuickEntryTypePopover.vue';
import QuickEntrySubjectPopover from './QuickEntrySubjectPopover.vue';
import { NOTE_TEXT_MAX_LENGTH } from 'common/constants/note.constants';
import { SUPPORT_MEASURE_DESCRIPTION_MAX_LENGTH } from 'common/constants/support.measure.constants';

const { currentTenant } = useCurrentTenant();

const emit = defineEmits<{
  'create-start': [];
  created: [payload: TQuickEntryCreatedPayload, postCreateAction?: TQuickEntryPostCreateAction];
  /** dialog variant only: asks the hosting dialog to close */
  close: [];
}>();

const props = withDefaults(
  defineProps<{
    /** Collapses the inline composer after an entry was created successfully. */
    collapseAfterCreate?: boolean;
    fixedContext?: IQuickEntryFixedContext;
    /** Overrides responsive detection when a hosting dialog snapshots its presentation on open. */
    mobileViewport?: boolean;
    preselection?: IQuickEntryPreselection;
    /** Explains when the active feed scope would exclude the saved draft. */
    visibilityHint?: IQuickEntryVisibilityHint;
    /** 'dialog' starts expanded, replaces collapse with a close request and disables click-outside */
    variant?: 'inline' | 'dialog';
  }>(),
  {
    collapseAfterCreate: false,
    fixedContext: undefined,
    mobileViewport: undefined,
    preselection: undefined,
    variant: 'inline',
    visibilityHint: undefined,
  },
);

const SUGGESTION_LISTBOX_ID = 'quick-entry-suggestions';

const ENTRY_TYPE_ICON_BY_KIND: Partial<Record<IQuickEntrySuggestionItem['kind'], Component>> = {
  [EQuickEntrySuggestionKind.Note]: DocumentPenIcon,
  [EQuickEntrySuggestionKind.SupportMeasure]: SupportMeasureIcon,
};

const COMMAND_ICON_BY_KIND: Partial<Record<IQuickEntrySuggestionItem['kind'], Component>> = {
  [EQuickEntrySuggestionKind.AllEligibleStudents]: GroupIcon,
  [EQuickEntrySuggestionKind.OpenCompetences]: CompetencesIcon,
  [EQuickEntrySuggestionKind.OpenStudents]: GroupIcon,
  [EQuickEntrySuggestionKind.OpenSubject]: BookIcon,
};

const { t } = useI18n();
const { allSchoolSubjects } = useSchoolSubject();
const { allPerformanceAssessmentsTeacher } = usePerformanceAssessment();
const { currentClass } = useCurrentClass();
const { allStudents, studentsLoaded } = useClassStudents(() => currentClass.value?.id);
const { allCompetenceDomains, isLoading: competenceDomainsLoading } = useCompetenceDomain();
const { currentCompetenceAssessment } = useCompetenceAssessment();
const { createPerformanceAssessmentEntry } = useCreatePerformanceAssessmentEntry();
const { createNote } = useNote();
const { create: createSupportMeasure } = useSupportMeasureEntry();
const { requestSupportMeasureSelection } = useQuickEntrySupportMeasure();
const { hasSupportMeasures } = useSupportMeasureFeature();
const { confirm } = useConfirmDialog();
const inlineQuickEntryComposerRegistry = useInlineQuickEntryComposerRegistry();
const performanceAssessmentEntryDetailDialog = useDialog({
  component: PerformanceAssessmentEntryDetailDialog,
  props: {},
});
const { showSupportMeasureEntryDialog, showSupportMeasureEntryDialogQueued } = useSupportMeasureEntryDialog();
const { isMobile } = useMobile();

const defaultStudentIds = computed(() => [...(props.preselection?.studentIds ?? [])]);
const fixedContext = toRef(props, 'fixedContext');
const studentSelectionReadOnly = computed(() => props.fixedContext?.studentIds !== undefined);
const subjectReadOnly = computed(() => props.fixedContext?.schoolSubjectId !== undefined);
const performanceAssessmentReadOnly = computed(() => props.fixedContext?.performanceAssessmentId !== undefined);
const competencesReadOnly = computed(() => props.fixedContext?.competenceIds !== undefined);

const { lastUsedQuickEntryType, lastUsedQuickEntryPerformanceAssessmentId, setLastUsedQuickEntryType } =
  useLastViewed();
const effectivePreselection = computed(() =>
  applyLastUsedQuickEntryType(
    props.preselection,
    { type: lastUsedQuickEntryType.value, performanceAssessmentId: lastUsedQuickEntryPerformanceAssessmentId.value },
    { fixedContext: props.fixedContext, hasSupportMeasures: hasSupportMeasures.value },
  ),
);

const draftComposable = useQuickEntryDraft({
  allSchoolSubjects,
  allPerformanceAssessments: allPerformanceAssessmentsTeacher,
  allStudents,
  allCompetenceDomains,
  defaultStudentIds,
  fixedContext,
});
const {
  draft,
  selectedSchoolSubject,
  selectedPerformanceAssessment,
  eligiblePerformanceAssessments,
  eligibleStudents,
  selectedStudents,
  resolvedStudents,
  resolvedStudentIds,
  competenceDomainsForSubject,
  selectedCompetences,
  showOverallRating,
  isPristine,
  nextValidationIssue,
  canSubmit,
  fixedContextValid,
} = draftComposable;

const requirementGuidance = useRequiredActionGuidance(nextValidationIssue, t);
const { announcement } = requirementGuidance;

const performanceAssessmentPrompt = requirementGuidance.promptForAction(QuickEntryRequiredAction.PerformanceAssessment);
const schoolSubjectPrompt = requirementGuidance.promptForAction(QuickEntryRequiredAction.SchoolSubject);
const studentsPrompt = requirementGuidance.promptForAction(QuickEntryRequiredAction.Students);
const textPrompt = requirementGuidance.promptForAction(QuickEntryRequiredAction.Text);

const rootRef = useTemplateRef('rootRef');
const textInputRef = useTemplateRef('textInputRef');
const contextRowRef = useTemplateRef('contextRowRef');
const bottomBarRef = useTemplateRef('bottomBarRef');
const subjectPopoverRef = useTemplateRef('subjectPopoverRef');
const typePopoverRef = useTemplateRef('typePopoverRef');
const competencePopoverRef = useTemplateRef('competencePopoverRef');
const datePopoverRef = useTemplateRef('datePopoverRef');

const isExpanded = ref(props.variant === 'dialog');
const { gradeConfiguration } = useGradeCalculationSettings(() => {
  if (
    !isExpanded.value ||
    selectedPerformanceAssessment.value?.documentationMode !== PerformanceAssessmentDocumentationMode.SCHOOL_GRADE
  ) {
    return null;
  }
  return currentTenant.value?.id;
});
const isSubmitting = ref(false);
const openPopover = ref<EQuickEntryPopover | null>(null);
const popoverAnchor = ref<HTMLElement | null>(null);
// Mobile always uses the same fullscreen composer, whether it was opened from a timeline dock
// or through the global QuickEntry dialog. The variant still controls close and success behavior.
const mobileViewportPresentation = computed(() => props.mobileViewport ?? isMobile.value);
const mobileViewportExpanded = computed(() => mobileViewportPresentation.value && isExpanded.value);
const suggestionsEnabled = computed(() => !isMobile.value && !mobileViewportPresentation.value);

const availableCompetenceDomainsForSelection = computed(() =>
  draft.schoolSubjectId != null ? competenceDomainsForSubject.value : allCompetenceDomains.value,
);

const selectedStudentIds = computed(() => selectedStudents.value.map((student) => student.id));

const suggestions = useQuickEntrySuggestions(
  {
    eligiblePerformanceAssessments,
    allSchoolSubjects,
    allStudents,
    eligibleStudents,
    selectedStudentIds,
    allEligibleStudentsSelected: computed(() => draft.allEligibleStudentsSelected),
    fixedContext,
  },
  {
    selectNote: draftComposable.selectNote,
    selectPerformanceAssessment: draftComposable.selectPerformanceAssessment,
    selectSupportMeasure: () => requestSupportMeasureSelection(draftComposable.selectSupportMeasure),
    selectSubject: draftComposable.selectSubject,
    addStudent: draftComposable.addStudent,
    selectAllEligibleStudents: draftComposable.selectAllEligibleStudents,
  },
  { enabled: suggestionsEnabled },
);

const isEligibilityReady = computed(
  () =>
    !!currentClass.value &&
    studentsLoaded.value &&
    !competenceDomainsLoading.value &&
    currentCompetenceAssessment.value !== null,
);

// the flexible scroll area only exists when it has something to hold
const showRatingArea = computed(
  () =>
    (showOverallRating.value && !!selectedPerformanceAssessment.value) ||
    (!!selectedCompetences.value.length && !!currentCompetenceAssessment.value),
);

const showSelectionHint = computed(
  () =>
    isEligibilityReady.value &&
    isPristine.value &&
    draft.allEligibleStudentsSelected &&
    draft.schoolSubjectId === null &&
    draft.performanceAssessmentId === null &&
    draft.competenceIds.length === 0,
);

const visibilityHintMessage = computed(() =>
  getQuickEntryVisibilityHint(props.visibilityHint, draft, resolvedStudentIds.value),
);

const showCollectiveRating = computed(() =>
  shouldShowQuickEntryCollectiveRating({
    competenceCount: selectedCompetences.value.length,
    hasCompetenceAssessment: currentCompetenceAssessment.value !== null,
    hasOverallRating: showOverallRating.value,
    hasSelectedPerformanceAssessment: selectedPerformanceAssessment.value !== null,
    kind: draft.kind,
    studentCount: resolvedStudents.value.length,
  }),
);

const wholeClassSelected = computed(() => isWholeClassStudentSelection(resolvedStudents.value, allStudents.value));

const textMaxLength = computed(() =>
  draft.kind === EQuickEntryKind.SupportMeasure ? SUPPORT_MEASURE_DESCRIPTION_MAX_LENGTH : NOTE_TEXT_MAX_LENGTH,
);

const activeOptionId = computed(() =>
  suggestions.isOpen.value ? getSuggestionOptionId(SUGGESTION_LISTBOX_ID, suggestions.activeIndex.value) : null,
);

watch(
  [
    () => effectivePreselection.value?.kind,
    () => effectivePreselection.value?.schoolSubjectId,
    () => effectivePreselection.value?.performanceAssessmentId,
    () => effectivePreselection.value?.studentIds?.join(','),
    () => effectivePreselection.value?.competenceIds?.join(','),
    () => props.fixedContext?.schoolSubjectId,
    () => props.fixedContext?.performanceAssessmentId,
    () => props.fixedContext?.studentIds?.join(','),
    () => props.fixedContext?.competenceIds?.join(','),
    () => props.fixedContext?.competenceDomainId,
    isEligibilityReady,
  ],
  () => {
    if (!isEligibilityReady.value) return;
    if (isPristine.value || !isExpanded.value) {
      requirementGuidance.reset();
      draftComposable.resetFromPreselection(effectivePreselection.value);
    }
  },
  { immediate: true },
);

watch(
  () => draft.kind,
  () => requirementGuidance.reset(),
);

const expand = async (): Promise<void> => {
  isExpanded.value = true;
  await nextTick();
  if (mobileViewportPresentation.value) return;
  textInputRef.value?.focus();
};

const handlePanelOpened = (): void => textInputRef.value?.focus();

const collapse = (): void => {
  requirementGuidance.reset();
  isExpanded.value = false;
  suggestions.close();
};

// the dialog variant has no collapsed state — closing the hosting dialog takes its place
const requestCollapse = (): void => {
  if (props.variant === 'dialog') {
    requirementGuidance.reset();
    emit('close');
    return;
  }
  collapse();
};

const popoverRefFor = (key: EQuickEntryPopover): { show: () => void; hide: () => void } | null => {
  switch (key) {
    case EQuickEntryPopover.Students:
      return {
        show: () => void contextRowRef.value?.showStudents(),
        hide: () => contextRowRef.value?.hideStudents(),
      };
    case EQuickEntryPopover.Subject:
      return subjectPopoverRef.value;
    case EQuickEntryPopover.EntryType:
      return typePopoverRef.value;
    case EQuickEntryPopover.Competences:
      return competencePopoverRef.value;
    case EQuickEntryPopover.Date:
      return datePopoverRef.value;
  }
};

const closeOpenPopover = (restoreFocus = false): void => {
  if (!openPopover.value) return;
  popoverRefFor(openPopover.value)?.hide();
  if (restoreFocus) popoverAnchor.value?.focus();
  openPopover.value = null;
};

const handleRequirementInteraction = (action: TQuickEntryRequiredAction): void => {
  requirementGuidance.interactWithAction(action);
};

const togglePopover = async (key: EQuickEntryPopover, anchor: HTMLElement): Promise<void> => {
  if (
    (key === EQuickEntryPopover.Students && studentSelectionReadOnly.value) ||
    (key === EQuickEntryPopover.Subject && subjectReadOnly.value) ||
    (key === EQuickEntryPopover.EntryType && performanceAssessmentReadOnly.value) ||
    (key === EQuickEntryPopover.Competences && competencesReadOnly.value)
  ) {
    return;
  }
  if (openPopover.value === key) {
    closeOpenPopover();
    return;
  }
  if (key === EQuickEntryPopover.Students) {
    handleRequirementInteraction(QuickEntryRequiredAction.Students);
  } else if (key === EQuickEntryPopover.Subject) {
    handleRequirementInteraction(QuickEntryRequiredAction.SchoolSubject);
  } else if (key === EQuickEntryPopover.EntryType) {
    handleRequirementInteraction(QuickEntryRequiredAction.PerformanceAssessment);
  }
  closeOpenPopover();
  suggestions.close();
  popoverAnchor.value = anchor;
  openPopover.value = key;
  await nextTick();
  popoverRefFor(key)?.show();
};

const handlePopoverHide = (key: EQuickEntryPopover): void => {
  if (openPopover.value === key) openPopover.value = null;
};

const handleStudentSelectionShow = (anchor: HTMLElement): void => {
  if (studentSelectionReadOnly.value) return;
  handleRequirementInteraction(QuickEntryRequiredAction.Students);
  if (openPopover.value !== EQuickEntryPopover.Students) closeOpenPopover();
  suggestions.close();
  popoverAnchor.value = anchor;
  openPopover.value = EQuickEntryPopover.Students;
};

const handleSubjectSelect = (schoolSubjectId: number): void => {
  draftComposable.selectSubject(schoolSubjectId);
  closeOpenPopover();
};

const handleNoteSelect = (): void => {
  draftComposable.selectNote();
  closeOpenPopover();
};

const handlePerformanceAssessmentSelect = (performanceAssessmentId: number): void => {
  draftComposable.selectPerformanceAssessment(performanceAssessmentId);
  closeOpenPopover();
};

const handleSupportMeasureSelect = (): void => {
  requestSupportMeasureSelection(() => {
    draftComposable.selectSupportMeasure();
    closeOpenPopover();
  });
};

const focusFromExternalAction = async (target?: TQuickEntryFocusTarget): Promise<void> => {
  closeOpenPopover();
  suggestions.close();
  await expand();

  // switches like the composer's own type chip: entered content stays, so there is nothing to confirm
  if (target && !isQuickEntryFocusTargetSelected(draft, target)) {
    if (target.kind === EQuickEntryKind.Note) {
      draftComposable.selectNote();
    } else if (target.kind === EQuickEntryKind.SupportMeasure) {
      requestSupportMeasureSelection(draftComposable.selectSupportMeasure);
    } else {
      const assessment = allPerformanceAssessmentsTeacher.value.find(({ id }) => id === target.performanceAssessmentId);
      // External selections may use another subject scope; adopt it, or clear the subject for shared assessments.
      if (assessment && !eligiblePerformanceAssessments.value.some(({ id }) => id === assessment.id)) {
        draftComposable.selectSubject(assessment.schoolSubjectId);
      }
      draftComposable.selectPerformanceAssessment(target.performanceAssessmentId);
    }
  }
};

const inlineQuickEntryComposerHandle: IInlineQuickEntryComposerHandle = {
  focus: focusFromExternalAction,
};

onMounted(() => {
  if (props.variant === 'inline') inlineQuickEntryComposerRegistry?.register(inlineQuickEntryComposerHandle);
});

onUnmounted(() => inlineQuickEntryComposerRegistry?.unregister(inlineQuickEntryComposerHandle));

const handleTextChange = (text: string): void => {
  handleRequirementInteraction(QuickEntryRequiredAction.Text);
  draftComposable.setText(text);
};

const handleOverallLevelChange = (value: number | null): void => {
  draftComposable.setOverallLevel(value);
};

const handleCompetenceLevelChange = (competenceId: number, value: number | null): void => {
  draftComposable.setCompetenceLevel(competenceId, value);
};

const handleCaretChange = (caretIndex: number): void => {
  suggestions.updateFromInput(draft.text, caretIndex);
};

const acceptSuggestion = async (item: Parameters<typeof suggestions.acceptItem>[0]): Promise<void> => {
  const result = suggestions.acceptItem(item);
  if (!result) return;
  await textInputRef.value?.setTextAndCaret(result.text, result.caretIndex);
  suggestions.updateFromInput(result.text, result.caretIndex);

  // selector commands open the same popovers as clicking their chips
  if (item.kind === EQuickEntrySuggestionKind.OpenCompetences) {
    const anchor = bottomBarRef.value?.competencesChip;
    if (anchor) await togglePopover(EQuickEntryPopover.Competences, anchor);
  }
  if (item.kind === EQuickEntrySuggestionKind.OpenStudents) {
    await contextRowRef.value?.showStudents();
  }
  if (item.kind === EQuickEntrySuggestionKind.OpenSubject) {
    const anchor = bottomBarRef.value?.subjectChip;
    if (anchor) await togglePopover(EQuickEntryPopover.Subject, anchor);
  }
};

const handleEscape = async (event?: Event): Promise<void> => {
  event?.preventDefault();
  event?.stopPropagation();

  // closing mid-save would drop the composer while the request is still in flight
  if (isSubmitting.value) return;

  if (event?.target instanceof HTMLElement && event.target.closest('.dp--menu-wrapper')) {
    if (props.variant === 'dialog') datePopoverRef.value?.hide();
    return;
  }

  const action = nextEscapeAction({
    suggestionsOpen: suggestions.isOpen.value,
    popoverOpen: openPopover.value !== null,
    isPristine: isPristine.value,
  });

  if (action === EQuickEntryEscapeAction.CloseSuggestions) {
    suggestions.close();
    return;
  }
  if (action === EQuickEntryEscapeAction.ClosePopover) {
    closeOpenPopover(true);
    return;
  }
  if (action === EQuickEntryEscapeAction.Collapse) {
    requestCollapse();
    return;
  }

  const discard = await confirm({
    title: t('quickEntry.confirmDiscard.title'),
    description: t('quickEntry.confirmDiscard.description'),
    confirmButtonLabel: t('quickEntry.confirmDiscard.confirmButtonLabel'),
    declineButtonLabel: t('quickEntry.confirmDiscard.declineButtonLabel'),
    theme: 'danger',
  });
  if (!discard) return;
  draftComposable.resetFromPreselection(effectivePreselection.value);
  requestCollapse();
};

const handleTextInputKeydown = (event: KeyboardEvent): void => {
  if (event.key === EKeyboardKey.Enter && (event.metaKey || event.ctrlKey)) {
    event.preventDefault();
    void submit();
    return;
  }
  if (suggestions.isOpen.value && (event.key === EKeyboardKey.Enter || event.key === EKeyboardKey.Tab)) {
    event.preventDefault();
    void acceptSuggestion(suggestions.items.value[suggestions.activeIndex.value]);
    return;
  }
  suggestions.handleSuggestionKeydown(event);
};

const finalizeCreated = async (
  payload: TQuickEntryCreatedPayload,
  postCreateAction?: TQuickEntryPostCreateAction,
): Promise<void> => {
  requirementGuidance.reset();
  setLastUsedQuickEntryType(draft.kind, draft.performanceAssessmentId);
  emit('created', payload, postCreateAction);
  draftComposable.resetFromPreselection(effectivePreselection.value);
  const shouldCollapse = mobileViewportExpanded.value || props.collapseAfterCreate;
  if (shouldCollapse) collapse();
  await requirementGuidance.announce(t('general.createSuccess', { count: 1 }));
  if (shouldCollapse) return;
  await nextTick();
  await textInputRef.value?.setTextAndCaret(draft.text, draft.text.length);
};

const createNoteEntry = async (schoolClassId: number): Promise<void> => {
  const dto = buildQuickEntryNoteCreateDto({
    draft,
    schoolClassId,
    studentIds: resolvedStudentIds.value,
  });
  const note = await createNote(dto, {
    noteCreationSource: ENoteCreationSource.Composer,
    suppressSuccessToast: props.variant === 'dialog',
  });
  await finalizeCreated({ type: ObservationTimelineEntryType.note, note });
};

const createSupportMeasureEntry = async (schoolClassId: number): Promise<void> => {
  if (draft.schoolSubjectId == null) return;
  const entry = await createSupportMeasure(
    buildQuickEntrySupportMeasureCreateDto({
      draft,
      schoolClassId,
      schoolSubjectId: draft.schoolSubjectId,
      studentIds: resolvedStudentIds.value,
    }),
    { creationSource: ESupportMeasureCreationSource.Composer },
  );
  await finalizeCreated({
    type: ObservationTimelineEntryType.supportMeasureEntry,
    supportMeasureEntry: entry,
  });
  if (props.variant !== 'inline') return;
  if (mobileViewportPresentation.value) showSupportMeasureEntryDialog(entry.id);
  else showSupportMeasureEntryDialogQueued(entry.id);
};

const createPerformanceAssessmentQuickEntry = async (
  schoolClassId: number,
  postCreateAction?: TQuickEntryPostCreateAction,
): Promise<void> => {
  const performanceAssessment = selectedPerformanceAssessment.value;
  const competenceAssessment = currentCompetenceAssessment.value;
  if (!performanceAssessment || !competenceAssessment || draft.schoolSubjectId == null) return;
  const dto = buildQuickEntryCreateDto({
    draft,
    schoolClassId,
    schoolSubjectId: draft.schoolSubjectId,
    performanceAssessmentId: performanceAssessment.id,
    documentationMode: performanceAssessment.documentationMode,
    competenceAssessmentId: competenceAssessment.id,
    label: getPerformanceAssessmentName(performanceAssessment, t),
    studentIds: resolvedStudentIds.value,
  });
  const entry = await createPerformanceAssessmentEntry(dto, {
    performanceAssessmentCreationSource: EPerformanceAssessmentCreationSource.Composer,
    suppressSuccessToast: props.variant === 'dialog',
  });
  await finalizeCreated(
    {
      type: ObservationTimelineEntryType.performanceAssessmentEntry,
      performanceAssessmentEntry: entry,
    },
    postCreateAction,
  );
  if (postCreateAction === 'open-details' && props.variant === 'inline') {
    await performanceAssessmentEntryDetailDialog.show({ performanceAssessmentEntryId: entry.id });
  }
};

const createSelectedEntry = async (
  schoolClassId: number,
  postCreateAction?: TQuickEntryPostCreateAction,
): Promise<void> => {
  switch (draft.kind) {
    case EQuickEntryKind.Note:
      await createNoteEntry(schoolClassId);
      break;
    case EQuickEntryKind.SupportMeasure:
      await createSupportMeasureEntry(schoolClassId);
      break;
    case EQuickEntryKind.PerformanceAssessment:
      await createPerformanceAssessmentQuickEntry(schoolClassId, postCreateAction);
      break;
  }
};

const submit = async (postCreateAction?: TQuickEntryPostCreateAction): Promise<void> => {
  if (isSubmitting.value || !isEligibilityReady.value || !fixedContextValid.value) return;
  if (!canSubmit.value) {
    const issue = await requirementGuidance.requestNext();
    if (issue?.action === QuickEntryRequiredAction.Text) textInputRef.value?.focus();
    return;
  }
  const schoolClass = currentClass.value;
  if (!schoolClass) return;

  requirementGuidance.reset();
  if (postCreateAction === 'open-details') {
    closeOpenPopover();
    suggestions.close();
  }
  emit('create-start');
  isSubmitting.value = true;
  try {
    await createSelectedEntry(schoolClass.id, postCreateAction);
  } finally {
    isSubmitting.value = false;
  }
};

onClickOutside(rootRef, (event) => {
  // in the dialog everything around the panel is the dialog itself — backdrop handling takes over
  if (props.variant === 'dialog') return;
  if (event.target instanceof HTMLElement && event.target.closest('.dp--menu-wrapper')) return;
  if (!isExpanded.value || openPopover.value !== null || isSubmitting.value) return;
  if (isPristine.value) collapse();
});

const datePickerTeleport = computed(() => resolveDialogTeleportTarget(popoverAnchor.value, rootRef.value));

defineExpose({
  expand,
  focusInput: (): void => textInputRef.value?.focus(),
  requestClose: async (event?: Event): Promise<void> => {
    if (isSubmitting.value) return;
    await handleEscape(event);
  },
});
</script>

<template>
  <section ref="rootRef" :aria-label="$t('quickEntry.expandLabel')" @keydown.esc="handleEscape">
    <ComposerPanel
      :accessible-label="$t('quickEntry.expandLabel')"
      :collapsed-label="$t('quickEntry.collapsedPlaceholder')"
      :collapsible="props.variant !== 'dialog'"
      :expanded="isExpanded"
      :mobile-viewport="mobileViewportPresentation"
      @expand="expand"
      @opened="handlePanelOpened"
      @request-collapse="handleEscape"
    >
      <ComposerContextRow
        ref="contextRowRef"
        class="shrink-0"
        :all-eligible-students-selected="draft.allEligibleStudentsSelected"
        :eligible-students="eligibleStudents"
        :selected-students="selectedStudents"
        :date="draft.date"
        :show-date="draft.kind !== EQuickEntryKind.SupportMeasure"
        :disabled="!isEligibilityReady"
        :mobile-drawer="mobileViewportPresentation"
        :date-popover-open="openPopover === EQuickEntryPopover.Date"
        :student-selection-read-only="studentSelectionReadOnly"
        :students-prompt="studentsPrompt"
        :show-close="!mobileViewportExpanded"
        :close-disabled="isSubmitting"
        @close="handleEscape"
        @hide:students="handlePopoverHide(EQuickEntryPopover.Students)"
        @interact:students="handleRequirementInteraction(QuickEntryRequiredAction.Students)"
        @select:all-eligible-students="draftComposable.selectAllEligibleStudents"
        @show:students="handleStudentSelectionShow"
        @toggle:date="(anchor) => togglePopover(EQuickEntryPopover.Date, anchor)"
        @update:students="(students) => draftComposable.setStudents(students.map((student) => student.id))"
      />

      <div :class="mobileViewportExpanded ? 'flex min-h-0 flex-1 flex-col' : 'contents'">
        <!-- desktop: the text takes the panel's spare height first (e.g. where the hint was), keeps
             its own scroll at MAX_HEIGHT_PX and never gives up height.
             mobile: it is one of two equal flex-1 areas, so text and competences split the screen. -->
        <ComposerInputRow
          :class="mobileViewportExpanded ? 'min-h-0' : 'shrink-0'"
          :fill-available-height="mobileViewportExpanded"
          :grow-available-height="!mobileViewportExpanded"
          :required-action-prompt="textPrompt"
          @click:surface="textInputRef?.focus()"
          @interact:required-action="handleRequirementInteraction(QuickEntryRequiredAction.Text)"
        >
          <ComposerTextInput
            ref="textInputRef"
            :fill-height="mobileViewportExpanded"
            :model-value="draft.text"
            :placeholder="$t('quickEntry.collapsedPlaceholder')"
            :max-length="textMaxLength"
            :listbox-id="suggestionsEnabled ? SUGGESTION_LISTBOX_ID : undefined"
            :listbox-open="suggestions.isOpen.value"
            :active-option-id="activeOptionId"
            @update:model-value="handleTextChange"
            @caret-change="handleCaretChange"
            @keydown="handleTextInputKeydown"
          />
          <SuggestionPanel
            v-if="suggestions.isOpen.value"
            :listbox-id="SUGGESTION_LISTBOX_ID"
            :options="suggestions.items.value"
            :active-index="suggestions.activeIndex.value"
            @select="acceptSuggestion"
            @update:active-index="suggestions.activeIndex.value = $event"
          >
            <template #leading="{ option }">
              <UserAvatar
                v-if="option.kind === EQuickEntrySuggestionKind.Student && option.student"
                class="!size-icon-md"
                :user="option.student"
              />
              <PerformanceAssessmentIcon
                v-else-if="
                  option.kind === EQuickEntrySuggestionKind.PerformanceAssessment && option.performanceAssessment
                "
                :symbol="option.performanceAssessment.symbol"
              />
              <SchoolSubjectBadge
                v-else-if="option.kind === EQuickEntrySuggestionKind.Subject && option.schoolSubject"
                :school-subject="option.schoolSubject"
              />
              <component
                :is="ENTRY_TYPE_ICON_BY_KIND[option.kind]"
                v-else-if="ENTRY_TYPE_ICON_BY_KIND[option.kind]"
                class="size-icon-md shrink-0 text-primary"
                aria-hidden="true"
              />
              <span
                v-else-if="COMMAND_ICON_BY_KIND[option.kind]"
                class="flex size-icon-md shrink-0 items-center justify-center text-primary"
              >
                <component :is="COMMAND_ICON_BY_KIND[option.kind]" class="size-icon-sm" aria-hidden="true" />
              </span>
            </template>
            <template #trailing="{ option }">
              <BillingProBadge
                v-if="option.kind === EQuickEntrySuggestionKind.SupportMeasure && !hasSupportMeasures"
                theme="dark"
                class="shrink-0"
              />
            </template>
          </SuggestionPanel>
        </ComposerInputRow>

        <!-- Keep ratings in one flexible scroll area. On mobile it may use at most half of the
             available composer height so the text input remains usable. Desktop needs a fixed cap
             instead: ComposerPanel has no definite height there, so a percentage would resolve to
             none and the panel would grow past the viewport. -->
        <div
          v-if="showRatingArea"
          class="mt-md min-h-0 overflow-y-auto"
          :class="mobileViewportExpanded ? 'max-h-[50%]' : 'max-h-composer-selection-list'"
        >
          <div class="overflow-hidden rounded-2xl bg-surface-secondary py-sm">
            <QuickEntryCollectiveRatingHint
              v-if="showCollectiveRating"
              :students="resolvedStudents"
              :whole-class-selected="wholeClassSelected"
            />

            <PerformanceAssessmentOverallRating
              v-if="showOverallRating && selectedPerformanceAssessment"
              :grade-configuration="gradeConfiguration"
              :performance-assessment="selectedPerformanceAssessment"
              :value="
                selectedPerformanceAssessment.documentationMode === PerformanceAssessmentDocumentationMode.SCHOOL_GRADE
                  ? draft.gradeValue
                  : draft.overallPerformanceLevelV2Id
              "
              @update:value="handleOverallLevelChange"
            />

            <div
              v-if="showOverallRating && selectedCompetences.length && currentCompetenceAssessment"
              class="px-md py-sm"
            >
              <CustomSeparator />
            </div>

            <template v-if="currentCompetenceAssessment">
              <template
                v-for="(selectedCompetence, index) in selectedCompetences"
                :key="selectedCompetence.competence.id"
              >
                <div v-if="index > 0" class="w-full px-md py-sm">
                  <CustomSeparator />
                </div>
                <SelectedCompetenceRatingRow
                  v-if="draft.kind === EQuickEntryKind.PerformanceAssessment"
                  :selected-competence="selectedCompetence"
                  :assessment="currentCompetenceAssessment"
                  :value="draft.competenceLevelById[selectedCompetence.competence.id] ?? null"
                  :removable="!competencesReadOnly"
                  @update:value="(value) => handleCompetenceLevelChange(selectedCompetence.competence.id, value)"
                  @remove="draftComposable.removeCompetence(selectedCompetence.competence.id)"
                />
                <SelectedCompetenceRow
                  v-else
                  :selected-competence="selectedCompetence"
                  :removable="!competencesReadOnly"
                  @remove="draftComposable.removeCompetence(selectedCompetence.competence.id)"
                />
              </template>
            </template>
          </div>
        </div>

        <InlineInfoHint v-if="visibilityHintMessage" class="mt-md shrink-0">
          {{ visibilityHintMessage }}
        </InlineInfoHint>

        <i18n-t
          v-if="showSelectionHint"
          keypath="quickEntry.selectionHint"
          tag="p"
          scope="global"
          class="mt-md w-full shrink-0 text-left text-footer text-secondary"
        >
          <template #personShortcut>
            <kbd
              class="inline-flex h-icon-sm min-w-icon-smd items-center justify-center rounded-checkbox border border-quaternary px-xs align-middle font-sans text-footer !leading-none"
              >@</kbd
            >
          </template>
          <template #commandShortcut>
            <kbd
              class="inline-flex h-icon-sm min-w-icon-smd items-center justify-center rounded-checkbox border border-quaternary px-xs align-middle font-sans text-footer !leading-none"
              >/</kbd
            >
          </template>
          <template #personExample>
            <strong class="font-bold">{{ $t('quickEntry.selectionHintPersonExample') }}</strong>
          </template>
          <template #subjectExample>
            <strong class="font-bold">{{ $t('quickEntry.selectionHintSubjectExample') }}</strong>
          </template>
        </i18n-t>
      </div>

      <QuickEntryBottomBar
        ref="bottomBarRef"
        class="mt-md shrink-0"
        :selected-performance-assessment="selectedPerformanceAssessment"
        :selected-school-subject="selectedSchoolSubject"
        :competence-count="selectedCompetences.length"
        :is-submitting="isSubmitting"
        :disabled="!isEligibilityReady || !fixedContextValid"
        :selected-kind="draft.kind"
        :performance-assessment-popover-open="openPopover === EQuickEntryPopover.EntryType"
        :subject-popover-open="openPopover === EQuickEntryPopover.Subject"
        :competence-popover-open="openPopover === EQuickEntryPopover.Competences"
        :show-individual-rating="showCollectiveRating"
        :mobile-expanded="mobileViewportExpanded"
        :performance-assessment-read-only="performanceAssessmentReadOnly"
        :subject-read-only="subjectReadOnly"
        :competences-read-only="competencesReadOnly"
        :performance-assessment-prompt="performanceAssessmentPrompt"
        :school-subject-prompt="schoolSubjectPrompt"
        @toggle:performance-assessment="(anchor) => togglePopover(EQuickEntryPopover.EntryType, anchor)"
        @toggle:subject="(anchor) => togglePopover(EQuickEntryPopover.Subject, anchor)"
        @toggle:competences="(anchor) => togglePopover(EQuickEntryPopover.Competences, anchor)"
        @remove:subject="draftComposable.selectSubject(null)"
        @clear:competences="draftComposable.clearCompetences"
        @interact:requirement="handleRequirementInteraction"
        @rate:individually="submit('open-details')"
        @collapse="handleEscape"
        @submit="submit"
      />
      <DateSelectionPopover
        ref="datePopoverRef"
        :anchor-element="popoverAnchor"
        :date="draft.date"
        :date-picker-teleport="datePickerTeleport"
        @update:date="draftComposable.setDate"
        @hide="handlePopoverHide(EQuickEntryPopover.Date)"
      />
    </ComposerPanel>

    <QuickEntrySubjectPopover
      v-if="!subjectReadOnly"
      ref="subjectPopoverRef"
      :anchor-element="popoverAnchor"
      :all-school-subjects="allSchoolSubjects"
      :mobile-drawer="mobileViewportPresentation"
      :selected-school-subject-id="draft.schoolSubjectId"
      @select:subject="handleSubjectSelect"
      @hide="handlePopoverHide(EQuickEntryPopover.Subject)"
    />
    <QuickEntryTypePopover
      v-if="!performanceAssessmentReadOnly"
      ref="typePopoverRef"
      :anchor-element="popoverAnchor"
      :eligible-performance-assessments="eligiblePerformanceAssessments"
      :mobile-drawer="mobileViewportPresentation"
      :selected-kind="draft.kind"
      :selected-performance-assessment-id="draft.performanceAssessmentId"
      @select:note="handleNoteSelect"
      @select:performance-assessment="handlePerformanceAssessmentSelect"
      @select:support-measure="handleSupportMeasureSelect"
      @hide="handlePopoverHide(EQuickEntryPopover.EntryType)"
    />
    <CompetenceSelectionPopover
      v-if="!competencesReadOnly"
      ref="competencePopoverRef"
      :anchor-element="popoverAnchor"
      :available-competence-domains="availableCompetenceDomainsForSelection"
      :all-school-subjects="allSchoolSubjects"
      :selected-competences="selectedCompetences.map((selected) => selected.competence)"
      :hide-school-subjects="draft.schoolSubjectId != null"
      :mobile-drawer="mobileViewportPresentation"
      @add:competence="draftComposable.addCompetence"
      @remove:competence="draftComposable.removeCompetence"
      @hide="handlePopoverHide(EQuickEntryPopover.Competences)"
    />
    <span class="sr-only" role="status" aria-live="polite">{{ announcement }}</span>
  </section>
</template>
